import { useState } from 'react';

import { QueueFilterValues, QueuePage } from '../api/types';
import NoShowShiftCard from '../components/NoShowShiftCard';
import QueueError from '../components/QueueError';
import QueueFilterHeader from '../components/QueueFilterHeader';
import QueuePagination from '../components/QueuePagination';
import ShiftDetailPanel from '../components/ShiftDetailPanel';
import useDashboardFilters from '../hooks/useDashboardFilters';
import useNoShowQueue from '../hooks/useNoShowQueue';
import useQueuePageClamp from '../hooks/useQueuePageClamp';

import LoadingState from '@/components/LoadingState';

// No-show queue tab: a "No show" title + Show control, then a card per shift
// with its outstanding no-show worker(s). Data comes from
// /api/admin/dashboard_v2/no_show.
//
// Filter state lives here, so this queue keeps its own Client/Skill/Location/
// Date-Range selections and cannot narrow either of the other two
// (GRAVY-10303). It is also part of the query key, so changing a selection
// rotates the cache slot rather than showing the previous result.

type Props = {
  agencyId: number;
  /** Owned by the page, so it survives tab switches (GRAVY-10303). */
  filters: QueueFilterValues;
  onFiltersChange: (next: QueueFilterValues) => void;
  /** Also owned by the page, for the same reason (GRAVY-10304). */
  page: QueuePage;
  onPageChange: (next: number) => void;
};

const NoShowTab = ({
  agencyId,
  filters,
  onFiltersChange,
  page,
  onPageChange,
}: Props) => {
  const { data, isLoading, isError, refetch } = useNoShowQueue(
    agencyId,
    filters,
    page,
  );
  const { data: filterOptions } = useDashboardFilters(agencyId);
  // A live refresh can shrink the queue under an admin working a later page,
  // stranding them on a page that no longer exists (GRAVY-10323).
  useQueuePageClamp(data?.pagination, page.page, onPageChange);

  const shifts = data?.shifts ?? [];
  // The card's chevron opens the shared panel OVER this queue, which stays
  // mounted — so closing returns to the same filters, page and scroll position
  // (GRAVY-10317). Same flow Missing Timesheet and Needs Approval already use.
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);

  return (
    <div>
      <QueueFilterHeader
        heading="No show"
        queue="no_show"
        options={filterOptions}
        value={filters}
        onChange={onFiltersChange}
      />

      {isLoading && !data ? (
        <LoadingState />
      ) : isError ? (
        <QueueError
          message="Unable to load the no-show queue."
          onRetry={refetch}
        />
      ) : shifts.length === 0 ? (
        <div className="dashv2-queue-empty">
          {data && data.total > 0
            ? 'No results on this page.'
            : 'No outstanding no-shows. 🎉'}
        </div>
      ) : (
        <div className="dashv2-queue-list">
          {shifts.map((shift) => (
            <NoShowShiftCard
              key={shift.shift_id}
              shift={shift}
              onSelect={setSelectedShiftId}
            />
          ))}
        </div>
      )}

      {data?.pagination && !isError && (
        <QueuePagination
          pagination={data.pagination}
          onPageChange={onPageChange}
          queue="No show"
        />
      )}

      {selectedShiftId != null && (
        <ShiftDetailPanel
          agencyId={agencyId}
          shiftId={selectedShiftId}
          onClose={() => setSelectedShiftId(null)}
        />
      )}
    </div>
  );
};

export default NoShowTab;
